<template>
  <div class="kt-subheader kt-grid__item" id="kt_subheader">
    <div class="kt-container justify-content-between" :class="{ 'kt-container--fluid': widthFluid }">
      <div class="d-flex align-items-center flex-nowrap mr-2">
        <slot name="before-title" />
        <!--begin::Title-->
        <slot name="title">
          <h3 v-if="title" class="text-dark font-weight-bold mt-2 mb-2">
            {{ title.slice(0, 42) + ((title.length > 42 && "...") || "") }}
          </h3>
          <a v-if="helpKey && helpLinks[helpKey]" :href="helpLinks[helpKey]" class="ml-2" target="_blank">
            <b-icon-question-circle />
          </a>
        </slot>
        <!--end::Title-->
        <slot name="after-title" />
        <div class="mr-3" />
        <!--begin::Search Form-->
        <div v-if="search" class="d-flex align-items-center" id="kt_subheader_search">
          <span class="text-dark-50 mr-3" id="kt_subheader_total">{{ search.total }}</span>
          <div class="ml-5 input-group input-group input-group-solid" style="max-width: 250px">
            <input
              v-model="searchText"
              @keyup.enter="find"
              type="text"
              class="form-control"
              id="kt_subheader_search_form"
              :placeholder="search.placeholder || 'Поиск...'"
            />
            <button @click="find" class="input-group-append">
              <span class="input-group-text h-100">
                <i class="flaticon2-search-1 icon" />
              </span>
            </button>
          </div>
        </div>
        <!--end::Search Form-->
      </div>
      <div class="d-flex align-items-center">
        <slot name="before-back-btn" />
        <button v-if="backBtn" @click="back" class="btn-lg btn btn-back px-3 py-1">
          {{ backBtn.caption || "Назад" }}
        </button>
        <slot name="after-back-btn" />
        <div v-if="buttons || dropdownBtns" class="btn-group">
          <!--begin::Button List-->
          <b-button
            v-for="(btn, index) in buttons"
            :key="btn.caption || btn.hint"
            :title="btn.hint"
            @click="btn.onClick"
            class="font-weight-bold px-3"
            size="lg"
            :variant="btn.variant || 'outline-primary'"
            :id="btn.tooltipContent ? `button-tooltip-id-${index}` : ''"
          >
            <i v-if="btn.flaticon" :class="[btn.flaticon, btn.caption ? '' : 'mr-0'].join(' ')" />
            {{ btn.caption }}
          </b-button>

          <b-popover
            v-for="(btn, index) in tooltipButtons"
            :target="`button-tooltip-id-${index}`"
            triggers="hover"
            :key="index"
            placement="top"
            :content="btn.tooltipContent"
          >
          </b-popover>

          <!--end::Button List-->
          <!--begin::Dropdown-->
          <template v-if="dropdownBtns">
            <button
              type="button"
              class="btn btn-primary font-weight-bold btn-lg px-3 dropdown-toggle dropdown-toggle-split"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            />
            <ul tabindex="-1" class="dropdown-menu dropdown-menu-right">
              <li v-for="item in dropdownBtns" :key="item.caption">
                <a href="javascript:;" target="_self" class="dropdown-item" @click="item.onClick">
                  <i v-if="item.flaticon" :class="item.flaticon" />
                  {{ item.caption }}
                </a>
              </li>
            </ul>
          </template>
          <!--end::Dropdown-->
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/sass/custom/colors.scss";
#kt_subheader {
  position: fixed;
  z-index: 95;
  .btn-back {
    color: #b5b5c3;
    background-color: #f3f6f9;
    border-color: #f3f6f9;
  }
  .btn-back:hover {
    color: $primary;
  }
  .btn.dropdown-toggle::after {
    margin-left: 0;
  }
  .dropdown-item:hover,
  .dropdown-item:hover i {
    color: $primary;
  }
  .dropdown-item:active {
    color: inherit;
    background-color: transparent;
    i {
      color: inherit;
    }
  }
  span.input-group-text {
    border-color: #c5cbd8;
  }
  .btn-group > .btn:not(.dropdown-toggle) {
    border-radius: 0.3rem;
  }
  .btn {
    font-size: 1.1rem !important;
    height: 38px !important;
  }
}
.kt-subheader {
  left: 0;
  right: 0;
}
</style>

<script>
import { mapGetters } from "vuex";
import { BIconQuestionCircle } from "bootstrap-vue";
import { config } from "@/main";

export default {
  name: "KTSubheader",
  props: {
    title: String,
    search: Object, // {total: int, findFun: callback, placeholder: str} - не предусмотрено использование total вместе со слотом after-title
    backBtn: Object /* {
      route: obj ({name: str} or {path: str}, ...),
      ?caption: str
    }*/,
    buttons: Array, // item: {?caption: str, ?hint: str, ?flaticon: str, ?variant: str, onClick: callback} - если caption нет, то hint должен быть и наоборот
    dropdownBtns: Array, // item: {caption: str, ?flaticon: str, onClick: callback}
    helpKey: String,
  },
  components: {
    BIconQuestionCircle,
  },
  data() {
    return {
      searchText: "",
      prevSearchText: "",
    };
  },
  mounted() {
    document.getElementById("kt_header").insertAdjacentElement("afterend", this.$el);
  },
  destroyed() {
    this.$el.remove();
  },
  computed: {
    ...mapGetters(["layoutConfig"]),
    ...mapGetters("account", ["isFree"]),
    /**
     * Check if subheader width is fluid
     */
    widthFluid() {
      return this.layoutConfig("subheader.width") === "fluid";
    },
    tooltipButtons() {
      return this.buttons.filter((item) => {
        return !!item.tooltipContent;
      });
    },
    helpLinks() {
      return config.helpLinks;
    }
  },
  methods: {
    find() {
      if (this.searchText === this.prevSearchText) {
        return;
      }
      this.prevSearchText = this.searchText;
      this.search.findFun(this.searchText);
    },
    back() {
      this.$router.push(this.backBtn.route);
    },
  },
};
</script>
